import React, { useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { base64ToFile } from '../../utils/fileHandling';
import { faArrowLeftLong, faChevronRight,faUpload } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; //direkte Unterstützung für Fußnoten, Durchstreichungen, Tabellen, Aufgabenlisten und URLs
import rehypeKatex from 'rehype-katex'; //um Mathematik darzustellen
import remarkMath from 'remark-math'; //Mathematik in Markdown zu unterstützen
import rehypeRaw from 'rehype-raw'; // HTML in markdown
import rehypeStringify from 'rehype-stringify';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeFormat from 'rehype-format';
import remarkEmoji from 'remark-emoji'; //display short cut emojis
import remarkCollapse from 'remark-collapse'; //not directly typed, make collapsable sections
import { ApiPDF } from '../../apis/ApiPDF';
import { Config } from '../../config/Config';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { AiDisclosureNotice } from '../../components/ui/AiDisclosureNotice';
import { useTrackedRun } from '../../hooks/useTrackedRun';
import { stateIconDone, stateIconError, stateIconWaiting } from '../../types/ai/states';
import { useNavigateBack } from '../../hooks/useNavigationBack';

//PDF: Saving in Backend
//PNG: Sending to API
interface RagBotProps {
    'imgSrc': string[] | null;
    'imgFiles':any[]|null;
    'pdfFile': any | null;
    'fileName': string | null;
    'userPrompt':string,
    'assistantResponse':string | null;
};

//TODO: image displaying
export const RagBot = () => {
    const { t } = useTranslation(['apps','toolsApps']);
    const navigateBack = useNavigateBack();
    const [inputs,setInputs] = useState<RagBotProps>({
        imgSrc:null,
        imgFiles:null,
        pdfFile: null,
        fileName: null,
        userPrompt:t('rag.demoPrompt'),
        assistantResponse:''
    });
    const [displayResult,setDisplayResult] = useState(false);
    const baseUrl = Config.json.AiServiceProxyUrl;
    const [state,setState]= useState<null|string>(null);
    const { startRun, completeRun, failRun, insufficientCreditsDialog } = useTrackedRun();

    const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
        setDisplayResult(false);
        const file = event.target.files ? event.target.files[0]  : null;
        setState('waiting');
        if(file){
            //TODO: Save PDF for User in Backend
            let formData = new FormData();
            formData.append('file',file);
            //PDF to PNG Konvertion
            let imgFileList: any[] = [];
            await ApiPDF.ConvertPdfToPng(formData)
            .then(data => {
                imgFileList = data;
                const imgSrc: string[] =  [];
                let pageNumber = 0;
                for(let imgBase64 of imgFileList){
                    try{
                        let img = base64ToFile(imgBase64,`${file.name}_${pageNumber}`);
                        let imgURL = URL.createObjectURL(img);
                        imgSrc.push(imgURL);
                    }
                    catch{
                        setState('error');
                    }
                    pageNumber ++;
                    
                }
                setInputs(prev => ({
                    ...prev,
                    ['imgSrc']:imgSrc,
                    ['imgFiles']:imgFileList,
                    ['pdfFile']: formData,
                    ['fileName']: file.name
                }));
                setState(null);
            })
            .catch(e =>{
                setState('error');
            });
            
        }
        else{
            setState('error');
        }
    };
    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputs(prev => ({...prev,[name]: value}));
    };
    const hanldeSendImageAndPrompt = async() => {
        setInputs(prev => ({
                ...prev,
                ['assistantResponse']: ""
            }));
        setState('waiting');
        const prompt = inputs.userPrompt ? inputs.userPrompt.trim() : 'Analyse this image and return text'.trim();
        const files = inputs.imgFiles; //base64 files
        if(!files){
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: t('missingImage')
            }));
            setState(null);
            setDisplayResult(true);
            return;
        }
        const body = {
            messages: [
                {
                    role: "user",
                    content: prompt,
                    images: files
                }
            ]
        };

        const runHandle = await startRun('vision', { estimatedCost: 0 });
        if (!runHandle) {
            // Either insufficient balance (dialog shown by useTrackedRun) or another failure -
            // fail closed, don't call cProxy without a tracked run.
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: "❌ " + t('error.requestFailed')
            }));
            setState(null);
            setDisplayResult(true);
            return;
        }

        try {
            const res = await fetchWithAuth(`${baseUrl}/vision`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body)
            });

            if(!res.ok){
                let err = await res.text();
                failRun(runHandle, `Vision request failed (HTTP ${res.status})`);
                setInputs(prev => ({
                    ...prev,
                    ['assistantResponse']: "❌ " + t('error.prefix') + err
                }));
                setState(null);
                setDisplayResult(true);
                return;
            }

            const data = await res.json();
            const usage = data.raw?.usage;
            completeRun(runHandle, {
                inputTokens: usage?.prompt_tokens ?? 0,
                outputTokens: usage?.completion_tokens ?? 0,
            });
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: data.reply || t('noResponse')
            }));
            setState(null);
            setDisplayResult(true);

        } catch(err){
            failRun(runHandle, `Vision request error: ${err}`);
            setInputs(prev => ({
                ...prev,
                ['assistantResponse']: "❌ " + t('error.connectionError')
            }));
            setState(null);
            setDisplayResult(true);
        }
    };

    useEffect(()=>{
        document.title = t('apps:registry.app.ragBot.name')
    },[]);

    return (
    <>
        {insufficientCreditsDialog}
        <div className="container-fluid p-4">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <button
                            onClick={() => navigateBack()}
                            className="btn btn-outline-secondary btn-sm ms-3"
                            title={t('toolsApps:back')}
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </button>
                    </div>
                </div>
                <div className='flex-grow-1 p-3 overflow-auto'>
                    {state
                        ? state === 'send'
                            ? <p className='h4'>{t('state.sending')}</p>
                            : state === 'waiting'
                                ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                                : state === 'error'
                                    ? <p className='h4'>{stateIconError} {t('state.error')}</p>
                                    : state === 'done'
                                        ? <p className='h4'>{stateIconDone} {t('state.done')}</p>
                                        : <></>
                        : <></>
                    }
                    { displayResult && (
                        <>
                            <div className="row mb-2">
                                <div className='col-auto p-3'>
                                    <div className='row'>
                                        <div className='col-auto h5'>
                                            {t('prompt')}
                                        </div>
                                        <div className='col'>
                                            {inputs.userPrompt}
                                        </div>
                                    </div>
                                </div>
                                <div className='col-1'></div>
                                <div className='col  p-3 '>
                                    { inputs.assistantResponse
                                        ?   <>
                                                <div className='row mb-1 h5'>{t('response')}</div>
                                                <div>
                                                    <ReactMarkdown 
                                                        remarkPlugins={[remarkMath,remarkEmoji,remarkGfm, remarkParse,remarkRehype,[remarkCollapse,{test:'heading'}]]} 
                                                        rehypePlugins={[rehypeKatex,rehypeFormat,rehypeRaw, rehypeStringify]}
                                                    >
                                                            {inputs.assistantResponse}
                                                    </ReactMarkdown>
                                                </div>
                                                <AiDisclosureNotice />
                                            </>
                                        : <></>
                                    }
                                </div>
                            </div>
                        </>
                        )}
                </div>
                
                <div className='row mt-2 me-5'>
                    <div className='col-auto'>
                        <label htmlFor="fileuploadImage" className=' fw-bold pe-2'>
                            <FontAwesomeIcon icon={faUpload} className='me-2' />
                            {t('rag.uploadPdf')}
                        </label>
                        <input
                        id = 'fileuploadImage'
                        type= 'file'
                        accept='application/pdf'
                        onChange={event => uploadFile(event)}
                        hidden
                        />
                        {
                            inputs.fileName
                            ? <>{inputs.fileName}, {t('rag.numberOfPages')} {inputs.imgFiles?.length}</>
                            : <></>
                        }
                    </div>
                    
                </div>
                 <div className="row mt-2">
                    <div className="col">
                        <textarea
                            id="chat-input-vision"
                            className="form-control border border-secondary border-1 bg-transparent"
                            name="userPrompt"
                            placeholder={t('imagePromptPlaceholder')}
                            onChange={handleInputChange}
                            style={{ resize: 'none' }}
                            value={inputs.userPrompt}
                        />
                    </div>
                    <div
                    className="col-auto d-flex align-items-center"
                    onClick={() => hanldeSendImageAndPrompt()}
                    >
                        <button
                            className="btn-chat bg-transparent border border-secondary rounded-circle text-secondary"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title={t('send')}
                            disabled={inputs.imgSrc == null || state === 'waiting'}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </>
  );
};

export default RagBot;